import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface FAQSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const faqSections = [
  {
    title: "Shipping and Delivery",
    items: [
      {
        question: "What are your shipping options and delivery times?",
        answer: "We offer standard and express shipping options. Standard delivery typically takes 3-5 business days within Egypt. Express shipping ensures delivery within 1-2 business days. International shipping times vary by destination."
      },
      {
        question: "Do you offer international shipping?",
        answer: "Yes, we ship to select international destinations. Shipping times and costs vary by location. Please check our shipping policy for detailed information about international delivery options and restrictions."
      }
    ]
  },
  {
    title: "Returns and Exchanges",
    items: [
      {
        question: "What is your return policy?",
        answer: "We accept returns within 30 days of purchase for unworn items in original condition with tags attached. Returns must be initiated through your account or by contacting customer service."
      },
      {
        question: "How do I exchange an item?",
        answer: "To exchange an item, please initiate a return through your account and place a new order for the desired item. This ensures the fastest processing time and item availability."
      }
    ]
  },
  {
    title: "Product Information",
    items: [
      {
        question: "How do I care for my TEL U ASEGH items?",
        answer: "Each item comes with specific care instructions. Generally, we recommend gentle hand washing or dry cleaning for delicate items. Please refer to our Product Care guide for detailed instructions."
      },
      {
        question: "Are your products sustainable?",
        answer: "Yes, we are committed to sustainability. We use eco-friendly materials and ethical manufacturing processes. Each product is crafted with attention to environmental impact and longevity."
      }
    ]
  },
  {
    title: "Orders and Payment",
    items: [
      {
        question: "What payment methods do you accept?",
        answer: "We accept major credit cards, PayPal, and bank transfers. All transactions are secured with industry-standard encryption for your safety."
      },
      {
        question: "Can I modify or cancel my order?",
        answer: "Orders can be modified or cancelled within 1 hour of placement. Please contact customer service immediately if you need to make changes to your order."
      }
    ]
  }
];

const FAQSectionComponent = ({ section, index }: { section: typeof faqSections[0], index: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="mb-8"
  >
    <h3 className="text-lg font-cinzel font-bold text-primary mb-4">{section.title}</h3>
    <Accordion type="single" collapsible className="space-y-2">
      {section.items.map((item, itemIndex) => (
        <AccordionItem
          key={itemIndex}
          value={`${index}-${itemIndex}`}
          className="border rounded-lg data-[state=open]:bg-accent/5"
        >
          <AccordionTrigger className="hover:bg-accent/10 rounded-lg px-4 text-sm">
            {item.question}
          </AccordionTrigger>
          <AccordionContent className="px-4 text-sm text-muted-foreground">
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </motion.div>
);

export function FAQSheet({ isOpen, onClose }: FAQSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            Frequently Asked Questions
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions about our products, services, and policies.
          </p>
        </motion.div>

        {/* FAQ Sections */}
        <div className="space-y-8 mb-8">
          {faqSections.map((section, index) => (
            <FAQSectionComponent key={index} section={section} index={index} />
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Can't find what you're looking for? Our customer support team is here to help.
          </p>
          <p className="text-sm font-medium text-primary">
            Contact us at{" "}
            <a 
              href="mailto:cs@teluasegh.com"
              className="hover:underline"
            >
              cs@teluasegh.com
            </a>
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 